/**
 * The Macs that build iOS.
 *
 * Xcode does not run on Linux, so iOS builds happen on real Macs that dial in to this console.
 * These are people's desk machines, which shapes the whole screen: a Mac that has gone home is
 * the normal case, not a fault, so offline is stated plainly and without alarm. The one thing
 * worth shouting about is a Mac with no Xcode, because that is the only state where a build
 * will be accepted and then fail ten minutes later.
 */
import { useCallback, useEffect, useState } from 'react';
import { ApiError, api, type Agent, type User } from '../api';

function seen(at: number | null): string {
  if (!at) return 'never connected';
  const seconds = Math.round((Date.now() - at) / 1000);
  if (seconds < 60) return `${seconds}s ago`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)} min ago`;
  if (seconds < 86_400) return `${Math.floor(seconds / 3600)} h ago`;
  return new Date(at).toLocaleDateString();
}

export function Macs({ me, onToast }: { me: User; onToast: (message: string) => void }) {
  const [macs, setMacs] = useState<Agent[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [issued, setIssued] = useState<{ name: string; token: string } | null>(null);
  const isAdmin = me.role === 'admin';

  const reload = useCallback(async () => {
    try {
      setMacs(await api.agents());
      setError(null);
    } catch (failure) {
      setError((failure as ApiError).message);
    }
  }, []);

  // Offline is decided from a timestamp, so the list has to re-read to go stale on screen.
  useEffect(() => {
    void reload();
    const timer = setInterval(() => void reload(), 5000);
    return () => clearInterval(timer);
  }, [reload]);

  const change = async (mac: Agent, patch: Parameters<typeof api.updateAgent>[1], done: string) => {
    try {
      await api.updateAgent(mac.id, patch);
      onToast(done);
      void reload();
    } catch (failure) {
      onToast((failure as ApiError).message);
    }
  };

  return (
    <div className="inner">
      <header className="run-head">
        <div className="run-title"><h1>Build machines</h1></div>
        {isAdmin && (
          <div className="actions">
            <button type="button" className="btn" onClick={() => setAdding((open) => !open)}>
              {adding ? 'Cancel' : 'Add a Mac'}
            </button>
          </div>
        )}
      </header>

      {error && <div className="errors section"><span>{error}</span></div>}

      {issued && <TokenOnce name={issued.name} token={issued.token} onDone={() => setIssued(null)} />}

      {adding && (
        <AddMac
          onAdded={(agent) => {
            setAdding(false);
            setIssued({ name: agent.name, token: agent.token! });
            void reload();
          }}
          onError={onToast}
        />
      )}

      {macs.length === 0 && !error ? (
        <div className="empty">
          <h1>No Macs yet</h1>
          <p>Add one for each machine that builds iOS.</p>
        </div>
      ) : (
        <section className="section">
          <div className="macs">
            {macs.map((mac) => (
              <article className={mac.online ? 'mac' : 'mac away'} key={mac.id}>
                <span className={`mac-light ${mac.online ? 'on' : 'off'}`} aria-hidden="true" />

                <div className="mac-who">
                  <h2>{mac.name}</h2>
                  <p className="mac-where">
                    {mac.hostName ?? 'no hostname yet'}
                    {mac.osVersion ? <span className="mac-os">macOS {mac.osVersion}</span> : null}
                  </p>
                </div>

                <dl className="mac-facts">
                  <div className="fact">
                    <dt>State</dt>
                    <dd className={mac.online ? 'is-on' : ''}>
                      {!mac.enabled ? 'paused' : mac.online ? 'ready' : 'away'}
                    </dd>
                  </div>
                  <div className="fact">
                    <dt>Last heard</dt>
                    <dd>{seen(mac.lastSeenAt)}</dd>
                  </div>
                  <div className="fact">
                    <dt>Xcode</dt>
                    <dd className={mac.online && !mac.xcodeVersion ? 'is-wrong' : ''}>
                      {mac.xcodeVersion ?? (mac.lastSeenAt ? 'not installed' : '—')}
                    </dd>
                  </div>
                  <div className="fact">
                    <dt>Building</dt>
                    <dd>{mac.busyWith.length ? mac.busyWith.map((id) => `#${id}`).join(' ') : 'nothing'}</dd>
                  </div>
                </dl>

                {isAdmin && (
                  <div className="mac-actions">
                    <button type="button" className="btn quiet small"
                            onClick={() => void change(mac, { enabled: !mac.enabled },
                              mac.enabled ? `${mac.name} takes no new builds` : `${mac.name} is back in the rota`)}>
                      {mac.enabled ? 'Pause' : 'Resume'}
                    </button>
                    <button type="button" className="btn quiet small"
                            onClick={() => {
                              api.regenerateAgentToken(mac.id)
                                .then(({ token }) => setIssued({ name: mac.name, token }))
                                .catch((failure: ApiError) => onToast(failure.message));
                            }}>
                      New token
                    </button>
                    <RemoveMac mac={mac} onToast={onToast} onRemoved={reload} />
                  </div>
                )}
              </article>
            ))}
          </div>

          {macs.some((mac) => mac.online && !mac.xcodeVersion) && (
            <div className="warnings">
              <h3>Worth a look</h3>
              <p className="warning">
                A Mac is online with no Xcode. Install it there, then run{' '}
                <span className="mono">xcodebuild -runFirstLaunch</span>.
              </p>
            </div>
          )}
        </section>
      )}
    </div>
  );
}

/**
 * The token, shown once.
 *
 * It is stored hashed, exactly like a password, so this is the only moment it exists in
 * readable form. Said plainly rather than hidden behind a copy button that might not work.
 */
function TokenOnce({ name, token, onDone }: { name: string; token: string; onDone: () => void }) {
  const [copied, setCopied] = useState(false);
  const command = `export WL_AGENT_TOKEN=${token}`;

  return (
    <section className="section keynote">
      <h2>{name} is registered</h2>
      <p>Shown once. If it is lost, issue a new one.</p>
      <p className="fingerprint mono">{token}</p>
      <div className="actions">
        <button
          type="button"
          className="btn"
          onClick={() => {
            navigator.clipboard?.writeText(command).then(() => setCopied(true), () => setCopied(false));
          }}
        >
          {copied ? 'Copied' : 'Copy the export line'}
        </button>
        <button type="button" className="btn quiet" onClick={onDone}>Done</button>
      </div>
      <p className="note" style={{ marginTop: 12 }}>
        On the Mac: <span className="mono">~/.whitelabel/token</span>, chmod 600.
      </p>
    </section>
  );
}

function AddMac({ onAdded, onError }: { onAdded: (agent: Agent) => void; onError: (message: string) => void }) {
  const [busy, setBusy] = useState(false);

  return (
    <form
      className="form section"
      onSubmit={(event) => {
        event.preventDefault();
        const form = new FormData(event.currentTarget);
        setBusy(true);
        api.addAgent(String(form.get('name')).trim())
          .then(onAdded)
          .catch((failure: ApiError) => onError(failure.message))
          .finally(() => setBusy(false));
      }}
    >
      <div className="field">
        <label htmlFor="mac-name">Name</label>
        <input id="mac-name" name="name" type="text" required autoFocus placeholder="Maria's Mac" />
        <span className="hint">Whose desk it is on.</span>
      </div>
      <div className="actions">
        <button className="btn" type="submit" disabled={busy}>{busy ? 'Adding' : 'Add'}</button>
      </div>
    </form>
  );
}

function RemoveMac({ mac, onToast, onRemoved }: {
  mac: Agent;
  onToast: (message: string) => void;
  onRemoved: () => void;
}) {
  const [confirming, setConfirming] = useState(false);

  if (!confirming) {
    return (
      <button type="button" className="btn quiet small" onClick={() => setConfirming(true)}>Remove</button>
    );
  }
  return (
    <span className="mac-confirm">
      <button
        type="button"
        className="btn danger small"
        onClick={() => {
          api.removeAgent(mac.id)
            .then(() => { onToast(`${mac.name} removed`); onRemoved(); })
            .catch((failure: ApiError) => onToast(failure.message))
            .finally(() => setConfirming(false));
        }}
      >
        Remove {mac.name}
      </button>
      <button type="button" className="btn quiet small" onClick={() => setConfirming(false)}>Keep</button>
    </span>
  );
}
